// ProductViewModal.jsx
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from 'prop-types';
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";

// Services & Config
import { fetchProductBySlug, checkSalePageStatus } from "../../../../Services/ProductServices";
import { Image_URL } from "../../../../config/config";

// Components
import VerticalCarousel from "../../../Viewproduct/Carousel/Index.jsx";
import Spinner from "../../../common/loadingSpinnercomponent/Spinner.jsx";

// Redux Actions
import { addToCart } from "../../../../slices/CartSlice";

// Assets
import Mintpay from "../../../../assests/ProductCard/Mintpay.png";
import Koko from "../../../../assests/ProductCard/Koko.png";
import shoppingBagImage from "../../../../assests/shopping-bag 1.png";

// Contexts
import { useCurrency } from "../../../../contexts/CurrencyContext";

// Styles
import "./Modal.css";

const ProductViewModal = ({
  slug = '',
  onClose = () => {},
  preSelectedSize = '',
  preSelectedColor = ''
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State management
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(preSelectedSize);
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [selectedPCID, setSelectedPCID] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [selectedColor, setSelectedColor] = useState(preSelectedColor);
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const [colorImages, setColorImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isSaleEnabled, setIsSaleEnabled] = useState(false);

  const { getCurrencySymbol, isSriLanka } = useCurrency();

  // Window resize listener
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper functions
  const handleColorClick = (color) => {
    if (!color?.Sources) return;

    const images = color.Sources.split(",").map((source) => source.trim());
    const firstImage = images[0];
    setSelectedColor(color.Color);
    setSelectedColorId(color.ColorId);
    setSelectedPCID(color.PCID);
    setMainImage(firstImage);
    setColorImages(images);
  };

  const getUniqueSizes = (colors) => {
    if (!colors?.length) return [];
    const uniqueSizes = new Set();
    colors.forEach((color) => {
      uniqueSizes.add(color.Size);
    });
    return Array.from(uniqueSizes);
  };

  const getUniqueColors = () => {
    if (!product?.Colors) return [];
    const seenColors = new Set();
    return product.Colors.filter((color) => {
      if (!seenColors.has(color.Color) && (!selectedSize || color.Size === selectedSize)) {
        seenColors.add(color.Color);
        return true;
      }
      return false;
    });
  };

  // Sale status check
  useEffect(() => {
    const checkSaleStatus = async () => {
      try {
        const status = await checkSalePageStatus();
        setIsSaleEnabled(status.isEnabled);
      } catch (error) {
        console.error('Error checking sale status:', error);
        setIsSaleEnabled(false);
      }
    };
    checkSaleStatus();
  }, []);

  // Product fetch effect
  useEffect(() => {
    if (!slug) return;

    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const productDetails = await fetchProductBySlug(slug);

        if (!productDetails || !Array.isArray(productDetails) || !productDetails[0]) {
          console.error('Invalid product response:', productDetails);
          throw new Error('Product not found');
        }

        const productData = productDetails[0];
        productData.Slug = productData.Slug || productData.slug;

        if (!productData.Colors?.length) {
          throw new Error('No product variants available');
        }

        setProduct(productData);

        // Handle pre-selected variants
        const selectedVariant = productData.Colors.find(
          color => color.Size === preSelectedSize && color.Color === preSelectedColor
        ) || productData.Colors[0];

        setSelectedSize(selectedVariant.Size);
        setSelectedSizeId(selectedVariant.SizeId);
        setSelectedPCID(selectedVariant.PCID);
        handleColorClick(selectedVariant);

      } catch (error) {
        console.error("Error fetching product details:", error);
        setError(error.message || "Failed to load product details");
        toast.error(error.message || "Error loading product details");
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [slug, preSelectedSize, preSelectedColor]);

  // Modal keyboard and focus management
  useEffect(() => {
    const modalElement = document.getElementById('product-modal');
    if (!modalElement) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const focusableElementsString = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = modalElement.querySelectorAll(focusableElementsString);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault();
            lastFocusableElement.focus();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            firstFocusableElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    modalElement.addEventListener('keydown', handleTabKey);
    firstFocusableElement.focus();

    // Prevent background scrolling
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollBarWidth}px`;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      modalElement.removeEventListener('keydown', handleTabKey);
      document.body.style.paddingRight = '';
      document.body.style.overflow = 'auto';
    };
  }, [onClose]);

  // Event Handlers
  const handleSizeSelect = (size) => {
    if (!product?.Colors) return;

    if (size === selectedSize) {
      setSelectedSize("");
      setSelectedSizeId(null);
      setSelectedColor("");
      setSelectedColorId(null);
      setSelectedPCID(null);
      setColorImages([]);
      setMainImage("");
    } else {
      const sizeVariant = product.Colors.find(color => color.Size === size);
      setSelectedSize(size);
      setSelectedSizeId(sizeVariant?.SizeId);

      const availableColors = product.Colors.filter(
        (color) => color.Size === size
      );

      const colorMatch = availableColors.find(
        (color) => color.Color === selectedColor
      );

      if (colorMatch) {
        handleColorClick(colorMatch);
      } else if (availableColors.length > 0) {
        handleColorClick(availableColors[0]);
      }
    }
  };

  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (value > 0) {
      setQuantity(value);
    }
  };

  // Price calculation
  const getDisplayPrices = () => {
    if (!product) return { currentPrice: 0, originalPrice: null };

    let currentPrice = 0;
    let originalPrice = null;

    if (isSriLanka()) {
      // For Sri Lankan users
      if (isSaleEnabled && product.OfferPrice && parseFloat(product.OfferPrice) > 0) {
        currentPrice = parseFloat(product.OfferPrice);
        originalPrice = parseFloat(product.Price);
      } else {
        currentPrice = parseFloat(product.Price);
      }
    } else {
      // For International users
      if (isSaleEnabled && product.InternationalOfferPrice && parseFloat(product.InternationalOfferPrice) > 0) {
        currentPrice = parseFloat(product.InternationalOfferPrice);
        originalPrice = parseFloat(product.InternationalPrice || product.Price);
      } else {
        currentPrice = parseFloat(product.InternationalPrice || product.Price);
      }
    }

    return { currentPrice, originalPrice };
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }

    if (!selectedColor) {
      toast.error("Please select a color");
      return;
    }

    if (!selectedPCID) {
      toast.error("Please select a valid variant");
      return;
    }

    try {
      const selectedVariant = product.Colors.find(
        color => color.Size === selectedSize && color.Color === selectedColor
      );

      if (!selectedVariant) {
        toast.error("Selected combination is not available");
        return;
      }

      if (parseInt(selectedVariant.Qty) <= 0) {
        toast.error("Selected variant is out of stock");
        return;
      }

      const { currentPrice } = getDisplayPrices();

      const productToAdd = {
        ProductId: parseInt(product.ProductId),
        Title: product.Title,
        Price: product.Price,
        InternationalPrice: product.InternationalPrice,
        OfferPrice: product.OfferPrice,
        InternationalOfferPrice: product.InternationalOfferPrice,
        Color: selectedColor,
        ColorId: selectedColorId,
        Size: selectedSize,
        SizeId: selectedSizeId,
        PCID: selectedPCID,
        quantity: quantity,
        Image: mainImage,
        Slug: product.Slug || product.slug, // Include Slug
        currentPrice: currentPrice
      };

      dispatch(addToCart(productToAdd));
      toast.success("Product added to cart!");
      onClose();
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add product to cart");
    }
  };

  const handleSizeChartClick = () => {
    if (windowWidth <= 768) {
      handleViewDetailsClick();
    } else {
      setShowSizeChart(!showSizeChart);
    }
  };

  const handleViewDetailsClick = () => {
    onClose();
    navigate(`/product/${product.Slug || product.slug}`);
  };

  // Check for modal root
  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    console.error('Modal root element not found');
    return null;
  }

  // Loading state
  if (loading) {
    return ReactDOM.createPortal(
      <div className="product-modal-loading">
        <Spinner />
      </div>,
      modalRoot
    );
  }

  // Error state
  if (error) {
    return ReactDOM.createPortal(
      <div className="product-modal-error">
        <p>{error}</p>
        <button onClick={onClose}>Close</button>
      </div>,
      modalRoot
    );
  }

  // No product or slug
  if (!slug || !product) {
    return null;
  }

  const uniqueSizes = getUniqueSizes(product.Colors);
  const { currentPrice, originalPrice } = getDisplayPrices();
  const currencySymbol = getCurrencySymbol();
  const installmentPrice = Math.floor(currentPrice / 3);

  return ReactDOM.createPortal(
    <>
      <div
        id="product-modal"
        className="product-modal fade show"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-modal-title"
        style={{ display: "block" }}
        onClick={onClose}
      >
        <div
          className="product-modal-dialog modal-lg product-modal-dialog-centered"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="product-modal-content">
            <div className="product-modal-header">
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              ></button>
            </div>
            <form className="product-modal-view" onSubmit={handleSubmit}>
              <h1 className="mobile-title" id="product-modal-title">{product.Title}</h1>
              <div className="product-modal-body">
                <div className="row">
                  <div className="col-md-6 d-flex image-container">
                    <div className="product-carousel-container">
                      <VerticalCarousel
                        images={colorImages}
                        setMainImage={setMainImage}
                        isModal={true}
                      />
                    </div>
                    <div className="product-modal-main-image-container">
                      <img
                        src={`${Image_URL}products/thumbnails/${mainImage}`}
                        alt={product.Title}
                        onError={(e) => {
                          e.target.src = "https://jbadmin.alephaz.com/assets/img/placeholder-image.jpg";
                        }}
                        className="product-modal-main-image"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="product-modal-details">
                      <h1 id="product-modal-title">{product.Title}</h1>

                      <div className="product-modal-price-container">
                        {originalPrice ? (
                          <>
                            <p className="product-modal-original-price">
                              {currencySymbol} {originalPrice.toFixed(2)}
                            </p>
                            <p className="product-modal-offer-price">
                              {currencySymbol} {currentPrice.toFixed(2)}
                            </p>
                          </>
                        ) : (
                          <p className="product-modal-price">
                            {currencySymbol} {currentPrice.toFixed(2)}
                          </p>
                        )}
                      </div>

                      <p className="product-modal-installment">
                        or 3 installments of {currencySymbol} {installmentPrice.toFixed(2)} with
                      </p>
                      <div className="product-modal-payment-logos-container">
                        <img
                          src={Mintpay}
                          alt="MintPay"
                          className="product-modal-payment-logo me-2"
                        />
                        <p className="product-modal-installment">or</p>
                        <img
                          src={Koko}
                          alt="Koko"
                          className="product-modal-payment-logo"
                        />
                      </div>
                      <p className="product-modal-description">
                        {product.Description}
                      </p>
                      <p
                        className="product-modal-view-details"
                        onClick={handleViewDetailsClick}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleViewDetailsClick();
                        }}
                      >
                        View Details
                      </p>
                      <p className="product-modal-size-label">Size:</p>
                      <div className="product-modal-size-options">
                        {uniqueSizes.map((size) => {
                          const sizeOption = product.Colors.find(
                            (color) => color.Size === size
                          );
                          const isOutOfStock =
                            sizeOption && parseInt(sizeOption.Qty) === 0;
                          return (
                            <button
                              type="button"
                              key={size}
                              onClick={() => handleSizeSelect(size)}
                              className={`product-modal-size-button ${
                                selectedSize === size ? "selected" : ""
                              }`}
                              disabled={isOutOfStock}
                              title={isOutOfStock ? "Out of stock" : ""}
                            >
                              {size}
                            </button>
                          );
                        })}
                      </div>
                      <p
                        className="product-modal-view-size-chart"
                        onClick={handleSizeChartClick}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleSizeChartClick();
                        }}
                      >
                        {windowWidth > 768 ? (showSizeChart ? "Hide Size Chart" : "View Size Chart") : "View Size Chart"}</p>
                      {showSizeChart && windowWidth > 768 && (
                        <div className="product-modal-size-chart">
                          <img
                            src="https://www.glamly.com/cdn/shop/articles/MicrosoftTeams-image_46_1200x1200.png?v=1698044645"
                            alt="Size Chart"
                            className="product-modal-size-chart-image"
                          />
                        </div>
                      )}
                      <div className="product-modal-color-dots-container">
                        {getUniqueColors().map((color, index) => (
                          <div
                            key={index}
                            className={`product-modal-color-dot ${
                              selectedColor === color.Color ? "selected" : ""
                            }`}
                            style={{ backgroundColor: color.Color }}
                            title={color.Color}
                            onClick={() => handleColorClick(color)}
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleColorClick(color);
                            }}
                          ></div>
                        ))}
                      </div>
                      <div className="product-modal-quantity-cart">
                        <div className="product-modal-quantity-counter">
                          <button
                            type="button"
                            className="product-modal-quantity-btn-left"
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            aria-label="Decrease quantity"
                          >
                            <i className="fa-solid fa-minus"></i>
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={quantity}
                            onChange={handleQuantityChange}
                            className="product-modal-quantity-field"
                            aria-label="Product quantity"
                          />
                          <button
                            type="button"
                            className="product-modal-quantity-btn"
                            onClick={() => setQuantity(quantity + 1)}
                            aria-label="Increase quantity"
                          >
                            <i className="fa-solid fa-plus"></i>
                          </button>
                        </div>
                        <button
                          type="submit"
                          className="product-modal-add-to-cart"
                          aria-label="Add to cart"
                        >
                          <img
                            src={shoppingBagImage}
                            alt=""
                            className="product-modal-shopping-bag-icon"
                            aria-hidden="true"
                          />
                          <p>Add to Cart</p>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>,
    modalRoot
  );
};

ProductViewModal.propTypes = {
  slug: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  preSelectedSize: PropTypes.string,
  preSelectedColor: PropTypes.string
};

export default ProductViewModal;
